import { ChevronLeft, ChevronRight, Maximize, Pause, Play, RotateCcw, RotateCw, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { Episode, Series } from '../types';

interface PlayerProps {
  series: Series;
  episode: Episode;
  initialProgress: number;
  canPlayEpisode: (episode: Episode) => boolean;
  onClose: () => void;
  onProgress: (episodeId: string, progress: number) => void;
  onComplete: (episodeId: string) => void;
  onSelectEpisode: (episode: Episode) => void;
  onLockedNext: (episode: Episode) => void;
}

export function Player({
  series,
  episode,
  initialProgress,
  canPlayEpisode,
  onClose,
  onProgress,
  onComplete,
  onSelectEpisode,
  onLockedNext
}: PlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoAvailable, setVideoAvailable] = useState<boolean | null>(null);
  const [playing, setPlaying] = useState(false);
  const [fallbackSeconds, setFallbackSeconds] = useState(initialProgress * episode.durationSeconds);
  const [actualDuration, setActualDuration] = useState(episode.durationSeconds);

  const index = series.episodes.findIndex((item) => item.id === episode.id);
  const previous = series.episodes[index - 1];
  const next = series.episodes[index + 1];
  const progress = videoAvailable
    ? ((videoRef.current?.currentTime ?? 0) / Math.max(1, actualDuration))
    : fallbackSeconds / episode.durationSeconds;

  const expectedFilename = useMemo(() => `${episode.assetBase}.mp4`, [episode.assetBase]);

  useEffect(() => {
    setVideoAvailable(null);
    setPlaying(false);
    setFallbackSeconds(initialProgress * episode.durationSeconds);
    setActualDuration(episode.durationSeconds);
  }, [episode.id, episode.durationSeconds, initialProgress]);

  useEffect(() => {
    const fallbackTimer = window.setTimeout(() => {
      setVideoAvailable((current) => current ?? false);
    }, 700);
    return () => window.clearTimeout(fallbackTimer);
  }, [episode.video]);

  useEffect(() => {
    if (!playing || videoAvailable !== false) return;
    const interval = window.setInterval(() => {
      setFallbackSeconds((current) => {
        const nextSeconds = Math.min(episode.durationSeconds, current + 0.5);
        onProgress(episode.id, nextSeconds / episode.durationSeconds);
        if (nextSeconds >= episode.durationSeconds) {
          window.clearInterval(interval);
          setPlaying(false);
          onComplete(episode.id);
          window.setTimeout(() => advanceNext(), 350);
        }
        return nextSeconds;
      });
    }, 500);
    return () => window.clearInterval(interval);
    // advanceNext deliberately uses current props and is stable for the lifetime of an episode.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playing, videoAvailable, episode.id, episode.durationSeconds]);

  function togglePlayback() {
    if (videoAvailable) {
      const video = videoRef.current;
      if (!video) return;
      if (video.paused) {
        void video.play();
      } else {
        video.pause();
      }
      return;
    }
    setPlaying((current) => !current);
  }

  function seek(deltaSeconds: number) {
    if (videoAvailable) {
      const video = videoRef.current;
      if (!video) return;
      video.currentTime = Math.max(0, Math.min(actualDuration, video.currentTime + deltaSeconds));
      return;
    }
    setFallbackSeconds((current) => Math.max(0, Math.min(episode.durationSeconds, current + deltaSeconds)));
  }

  function advanceNext() {
    if (!next) return;
    if (canPlayEpisode(next)) {
      onSelectEpisode(next);
    } else {
      onLockedNext(next);
    }
  }

  function selectPrevious() {
    if (previous) onSelectEpisode(previous);
  }

  function handleTimelineChange(value: number) {
    if (videoAvailable) {
      const video = videoRef.current;
      if (video) video.currentTime = value * actualDuration;
    } else {
      setFallbackSeconds(value * episode.durationSeconds);
      onProgress(episode.id, value);
    }
  }

  return (
    <div className="player-backdrop" role="dialog" aria-modal="true" aria-label={`Playing ${series.title}, episode ${episode.number}`}>
      <div className="player-shell">
        <header className="player-header">
          <div>
            <span>{series.title}</span>
            <strong>S1 E{String(episode.number).padStart(2, '0')} · {episode.title}</strong>
          </div>
          <button className="icon-button" onClick={onClose} aria-label="Close player"><X /></button>
        </header>

        <div className="video-stage">
          <video
            key={episode.video}
            ref={videoRef}
            className={videoAvailable === false ? 'hidden-video' : 'video-element'}
            src={episode.video}
            poster={episode.thumbnail}
            playsInline
            preload="metadata"
            onLoadedMetadata={(event) => {
              const video = event.currentTarget;
              setVideoAvailable(true);
              setActualDuration(Number.isFinite(video.duration) ? video.duration : episode.durationSeconds);
              video.currentTime = initialProgress * (Number.isFinite(video.duration) ? video.duration : episode.durationSeconds);
            }}
            onError={() => setVideoAvailable(false)}
            onPlay={() => {
              if (videoAvailable === true) setPlaying(true);
            }}
            onPause={() => {
              if (videoAvailable === true) setPlaying(false);
            }}
            onTimeUpdate={(event) => {
              const video = event.currentTarget;
              if (video.duration) onProgress(episode.id, video.currentTime / video.duration);
            }}
            onEnded={() => {
              onComplete(episode.id);
              advanceNext();
            }}
          />

          {videoAvailable !== true && (
            <div className="fallback-player" style={{ backgroundImage: `url(${episode.thumbnail})` }}>
              <div className="fallback-shade" />
              <div className="fallback-copy">
                <span className="demo-label">Prototype playback</span>
                <h2>{episode.title}</h2>
                <p>
                  Add <code>{expectedFilename}</code> to <code>public/videos/</code> to use the real video.
                  Until then, this screen simulates playback so progress, autoplay and unlocking can be tested.
                </p>
              </div>
            </div>
          )}

          <button className="central-play" onClick={togglePlayback} aria-label={playing ? 'Pause' : 'Play'}>
            {playing ? <Pause fill="currentColor" /> : <Play fill="currentColor" />}
          </button>
        </div>

        <div className="player-controls">
          <input
            className="timeline"
            type="range"
            min="0"
            max="1"
            step="0.001"
            value={Math.max(0, Math.min(1, progress || 0))}
            onChange={(event) => handleTimelineChange(Number(event.target.value))}
            aria-label="Playback position"
          />
          <div className="control-row">
            <div className="control-cluster">
              <button onClick={selectPrevious} disabled={!previous} aria-label="Previous episode"><ChevronLeft /></button>
              <button onClick={() => seek(-10)} aria-label="Back 10 seconds"><RotateCcw /></button>
              <button className="player-primary-control" onClick={togglePlayback} aria-label={playing ? 'Pause' : 'Play'}>
                {playing ? <Pause fill="currentColor" /> : <Play fill="currentColor" />}
              </button>
              <button onClick={() => seek(10)} aria-label="Forward 10 seconds"><RotateCw /></button>
              <button onClick={advanceNext} disabled={!next} aria-label="Next episode"><ChevronRight /></button>
            </div>
            <button
              onClick={() => document.querySelector('.player-shell')?.requestFullscreen?.()}
              aria-label="Full screen"
            ><Maximize /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
